<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'yieldManagement' }">{{ t('back') }}</ButtonBack>
  </ButtonBackContainer>

  <section class="carvia-container">
    <EditYieldManagementOtherForm
      v-if="settingsDto"
      :settings-dto="settingsDto"
      @save="handleSave"
    />
  </section>
</template>

<script lang="ts" setup>
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import {
  useUpdateYieldManagementSettings,
  useYieldManagementSettings,
} from '@/queries/use-yield-management-settings.ts';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import { computed, watchEffect } from 'vue';
import { useUiStore } from '@/stores/ui.store.ts';
import { useI18n } from 'vue-i18n';
import { useMoneyClicks } from '@/hooks/use-money-clicks.ts';
import type { UpdateYieldManagementSettingsDto } from '@carvia/ros-client-types';
import { Alert } from '@/services/alert.service';
import EditYieldManagementOtherForm from '@/views/yield-management/yield-management-settings/EditYieldManagementOtherForm.vue';

const { t } = useI18n();
const uiStore = useUiStore();

watchEffect(() => {
  uiStore.setHeaderTitle(t('otherYieldMangementSettings'));
});

const { data: settings } = useYieldManagementSettings();

const settingsDto = computed<UpdateYieldManagementSettingsDto | undefined>(() =>
  settings.value
    ? {
        deposits: settings.value.deposits,
        minAges: settings.value.minAges,
        fuelingPricePerLiter: settings.value.fuelingPricePerLiter,
        chargingPricePerKwh: settings.value.chargingPricePerKwh,
        youngDriverUntilAge: settings.value.youngDriverUntilAge,
        debitBookingCategories: settings.value.debitBookingCategories,
        commissionPercentageOptions: settings.value.commissionPercentageOptions,
        discountPercentageOptions: settings.value.discountPercentageOptions,
        cleaningFees: settings.value.cleaningFees,
        infringementFees: settings.value.infringementFees,
        vatRates: (settings.value.vatRates ?? []).map((entry) => ({
          ...entry,
          validFrom: entry.validFrom ?? null,
        })),
        crossborderCountries: settings.value.crossborderCountries,
        disabledInspectionShotKeys: settings.value.disabledInspectionShotKeys,
        requireInspectionStationIds: settings.value.requireInspectionStationIds,
        stationTransferDiscountModifier: settings.value.stationTransferDiscountModifier,
        stationTransferPriceLimitModifier: settings.value.stationTransferPriceLimitModifier,
      }
    : undefined,
);

const { mutateAsync: updateSettings } = useUpdateYieldManagementSettings();

const handleSave = async (values: UpdateYieldManagementSettingsDto) => {
  const { isConfirmed } = await Alert.fire({
    text: t('reallySaveChanges'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('saveChanges'),
    denyButtonText: t('cancel'),
  });
  if (!isConfirmed) {
    return;
  }
  await updateSettings({
    ...values,
    infringementFees: values.infringementFees?.map((entry) => ({
      amount: entry.amount,
      countries: entry.countries?.length ? entry.countries : null,
    })),
    crossborderCountries: values.crossborderCountries,
  });
};

useMoneyClicks();
</script>

<i18n lang="json">
{
  "en": {
    "otherYieldMangementSettings": "Other 🤑💸💶💰💵 settings",
    "reallySaveChanges": "Do you really want to save? This will change prices for all bookings from now on.",
    "saveChanges": "Save changes"
  },
  "de": {
    "otherYieldMangementSettings": "Sonstige 🤑💸💶💰💵 Einstellungen",
    "reallySaveChanges": "Möchtest du wirklich speichern? Dies wird die Preise für alle Buchungen ab jetzt ändern.",
    "saveChanges": "Änderungen speichern"
  },
  "es": {
    "otherYieldMangementSettings": "Otros ajustes 🤑💸💶💰💵",
    "reallySaveChanges": "¿Seguro que quieres guardar? Cambiará los precios para todas las reservas a partir de ahora.",
    "saveChanges": "Guardar cambios"
  }
}
</i18n>
